"use client";

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from "react";
import { X } from "lucide-react";
import { useDict } from "@/components/i18n/DictProvider";
import { cn } from "@/components/ui/cn";

// Nothing external mutates the flag after load (only our own click, which has
// its own state), so the store never notifies.
const subscribeNever = () => () => {};

/**
 * What the wrapper knows about itself, for what renders inside it.
 *
 * `settled` is true from the first post-hydration render on — the same render
 * in which the session flag below is first accurate (both are external-store
 * reads whose server snapshot differs, so React re-renders them together).
 * Until then the placement must NOT count an impression: the server snapshot
 * always shows the banner, and a child's mount effect runs before the parent
 * can unmount it, so a banner dismissed earlier in the session would still be
 * billed once per page view. Outside a wrapper there is nothing to wait for.
 *
 * `dismiss` is what the close control calls; `label` names it.
 */
const DismissContext = createContext<{
  settled: boolean;
  dismiss: () => void;
  label: string;
} | null>(null);

export function useDismissSettled(): boolean {
  const ctx = useContext(DismissContext);
  return ctx ? ctx.settled : true;
}

/**
 * Session-scoped dismiss for one ad surface: renders its children until the
 * visitor closes it, then nothing at all — not a wrapper, not a spacer — and
 * remembers the choice in sessionStorage per key, so the surface stays away
 * for the visit and returns on the next one. Pure state: it adds no element
 * of its own, so whatever it wraps (a whole page section, a grid cell, a fixed
 * unit) is what disappears, and the page closes up as if the slot had come
 * back unfilled. The close control itself is `<DismissControl>`, placed by the
 * frame where the design wants it (components/placements/PlacementFrame.tsx),
 * and wired through context.
 *
 * Renders children untouched until mounted state is known — the first paint
 * matches the server HTML (surface visible), then a dismissed one collapses.
 * The placement inside learns through `useDismissSettled` when that moment has
 * passed, so a dismissed banner never counts.
 */
export function DismissableAd({
  storageKey,
  children,
}: {
  storageKey: string;
  children: ReactNode;
}) {
  const t = useDict();
  const key = `lh.adDismissed.${storageKey}`;
  // Server snapshot says "visible" (matches the SSR HTML); after hydration the
  // client snapshot reads the session flag without a mismatch. Storage blocked
  // → the surface simply stays, which is the safe default.
  const stored = useSyncExternalStore(
    subscribeNever,
    () => {
      try {
        return !!sessionStorage.getItem(key);
      } catch {
        return false;
      }
    },
    () => false,
  );
  const [closed, setClosed] = useState(false);
  // false while hydrating, true on the client thereafter — no setState in an
  // effect, and it flips in the same render `stored` becomes trustworthy.
  const settled = useSyncExternalStore(
    subscribeNever,
    () => true,
    () => false,
  );
  const dismiss = useCallback(() => {
    try {
      sessionStorage.setItem(key, "1");
    } catch {
      // Still dismiss for this render even when storage is blocked.
    }
    setClosed(true);
  }, [key]);
  const label = t.pages.adClose;
  const state = useMemo(() => ({ settled, dismiss, label }), [settled, dismiss, label]);

  if (closed || stored) return null;

  return <DismissContext.Provider value={state}>{children}</DismissContext.Provider>;
}

/**
 * The close control: a quiet "×" the size of a caption line, always visible
 * (a hover-only control is unreachable on a touch screen, and a visitor who
 * wants an ad gone should not have to hunt for the way). Renders nothing
 * outside a `<DismissableAd>` — a surface the author made non-dismissible, or
 * the editor canvas, where an author must not put away their own block.
 */
export function DismissControl({ className }: { className?: string }) {
  const ctx = useContext(DismissContext);
  if (!ctx) return null;
  return (
    <button
      type="button"
      title={ctx.label}
      aria-label={ctx.label}
      onClick={ctx.dismiss}
      className={cn(
        // 24 px drawn, 44 px to hit: the pseudo-element extends the target without
        // growing the label line the mark sits on (WCAG target size; the reader
        // strip's controls do the same on touch screens).
        "text-muted-foreground hover:text-foreground hover:bg-muted focus-visible:ring-ring relative inline-flex size-6 shrink-0 items-center justify-center rounded-full transition-colors before:absolute before:-inset-2.5 before:content-[''] focus-visible:outline-none focus-visible:ring-2",
        className,
      )}
    >
      <X className="size-3.5" strokeWidth={2} aria-hidden />
    </button>
  );
}
